import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createDataSource } from '../../api/auth';
import { fetchDataSources } from '../../store/slices/dataSourcesSlice';

function CreateBYODSourceModal({ onClose }) {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [tablePrefix, setTablePrefix] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await createDataSource({
        name: name.trim(),
        table_prefix: tablePrefix.trim(),
        source_type: 'file',
      });
      dispatch(fetchDataSources());
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create data source';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="create-byod-modal">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add File Data Source</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Create a file-based data source to upload CSV or Excel files as queryable tables.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="byod-modal-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-byod-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My File Source"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="byod-source-name-input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Table Prefix <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={tablePrefix}
              onChange={(e) => setTablePrefix(e.target.value)}
              placeholder="e.g. my_files (letters, digits, underscores)"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="byod-table-prefix-input"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Used as a prefix for DuckDB tables (e.g. <code>my_files__customers</code>).
              Must start with a letter or underscore, max 20 characters.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !tablePrefix.trim()}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="byod-create-submit-btn"
            >
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateBYODSourceModal;
